import { ExternalLink } from "@/components/ExternalLink";
import { formatNumber } from "@/features/Table/utils";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { Tables } from "@/gen/tableNames/tables";
import i18n from "@/i18n";
import { parseDate } from "@/lib/utils";
import { createEnum } from "@/lib/utils/createEnum";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";

export type S1Position = {
  id: number;
  strategy_from_id?: number | null;
  strategy_from_name?: string | null;
  strategy_to_id?: number | null;
  strategy_to_name?: string | null;
  quantity?: number | null;
  date?: string | null;
  weight?: number | null;
  market_value_local_ccy?: number | null;
  market_value_ptf_ccy?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export const s1PositionFields = createEnum<S1Position>();

const t = (key: string, defaultValue: string) =>
  i18n.t(`${Tables.S1_POSITIONS}.columns.${key}`, {
    ns: "tables",
    defaultValue,
  });

function StrategyLink({
  id,
  name,
}: {
  id?: number | null;
  name?: string | null;
}) {
  if (!id) return "";
  return (
    <ExternalLink
      href={toWorkspacePath(`/admin/qtl - referential/strategies/${id}`)}
      className="text-xs"
      underline="hover"
    >
      {name || id}
    </ExternalLink>
  );
}

export const s1_positions_columns: ColumnDef<S1Position>[] = [
  {
    accessorKey: s1PositionFields.id,
    header: () => t(s1PositionFields.id, "ID"),
  },
  {
    accessorKey: s1PositionFields.date,
    header: () => t(s1PositionFields.date, "Date"),
    cell: ({ row }) => {
      const value = row.getValue(s1PositionFields.date);
      return (
        <div className="whitespace-nowrap">
          {value ? parseDate(String(value)) : ""}
        </div>
      );
    },
  },
  {
    accessorKey: s1PositionFields.strategy_from_name,
    header: () => t(s1PositionFields.strategy_from_name, "Strategy from"),
    cell: ({ row }) => (
      <StrategyLink
        id={row.original.strategy_from_id}
        name={row.original.strategy_from_name}
      />
    ),
  },
  {
    accessorKey: s1PositionFields.strategy_to_name,
    header: () => t(s1PositionFields.strategy_to_name, "Strategy to"),
    cell: ({ row }) => (
      <StrategyLink
        id={row.original.strategy_to_id}
        name={row.original.strategy_to_name}
      />
    ),
  },
  {
    accessorKey: s1PositionFields.quantity,
    header: () => (
      <div className="text-right">
        {t(s1PositionFields.quantity, "Quantity")}
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue(s1PositionFields.quantity))}
      </div>
    ),
  },
  {
    accessorKey: s1PositionFields.weight,
    header: () => (
      <div className="text-right">{t(s1PositionFields.weight, "Weight")}</div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue(s1PositionFields.weight))}
      </div>
    ),
  },
  {
    accessorKey: s1PositionFields.market_value_local_ccy,
    header: () => (
      <div className="text-right">
        {t(s1PositionFields.market_value_local_ccy, "Market value (local ccy)")}
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue(s1PositionFields.market_value_local_ccy))}
      </div>
    ),
  },
  {
    accessorKey: s1PositionFields.market_value_ptf_ccy,
    header: () => (
      <div className="text-right">
        {t(
          s1PositionFields.market_value_ptf_ccy,
          "Market value (reporting ccy)",
        )}
      </div>
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue(s1PositionFields.market_value_ptf_ccy))}
      </div>
    ),
  },
  {
    id: "actions",
    cell: ({ row }) => (
      <div className="flex justify-end">
        <Link
          href={`${window.location.pathname}/${row.getValue(s1PositionFields.id)}`}
          className="text-xs"
          underline="hover"
        >
          {i18n.t("actions.edit")}
        </Link>
      </div>
    ),
  },
];
